import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Notifier from 'components/ui/Notifier';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Table from 'components/ui/Table';
import { useAuth } from 'context/AuthContext';
import { employerAPI } from 'utils/api';
import { useConfirm } from 'context/ConfirmContext';

const STATUS_LABELS = {
  0: { label: 'Active', color: 'bg-blue-100 text-blue-700' },
  1: { label: 'Expired', color: 'bg-red-100 text-red-700' },
  2: { label: 'Submitted', color: 'bg-green-100 text-green-700' },
  3: { label: 'Released', color: 'bg-gray-100 text-gray-500' }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const EmployerShortlist = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const [summary, setSummary] = useState({ active: 0, expired: 0, submitted: 0, released: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vdrApplications, setVdrApplications] = useState({});
  const [selectedWorkers, setSelectedWorkers] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [notify, setNotify] = useState({ show: false, type: 'info', message: '' });

  const getPhotoSrc = (biodata) => {
    if (!biodata) return '/assets/images/no_image.png';
    return biodata.photo_url || biodata.photoUrl || '/assets/images/no_image.png';
  };

  const buildColumns = () => ([
    {
      label: 'Worker',
      accessor: 'worker',
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100 ring-1 ring-secondary-200 flex-shrink-0">
            <Image src={getPhotoSrc(row.biodata)} alt={row.biodata?.fullName || 'Worker'} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-semibold text-text-primary">{row.biodata?.fullName || 'Unknown Worker'}</div>
            <div className="text-xs text-text-secondary">Passport: {row.biodata?.passportNo || 'N/A'}</div>
          </div>
        </div>
      )
    },
    {
      label: 'Details',
      accessor: 'details',
      render: (row) => (
        <div className="text-sm text-text-secondary">
          <div className="font-semibold text-text-primary">{row.biodata?.nationality || 'N/A'}</div>
          <div>{row.biodata?.gender}</div>
        </div>
      )
    },
    {
      label: 'Reserved Until',
      accessor: 'reserved_until',
      render: (row) => (
        <div className="text-sm text-text-primary">{formatDate(row.holdExpiryDate || row.reserved_until)}</div>
      )
    },
    {
      label: 'Status',
      accessor: 'status',
      render: (row) => (
        <div className="text-sm">{renderStatusBadge(row.status)}</div>
      )
    }
  ])

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  const loadShortlists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await employerAPI.getShortlists();
      setShortlists(response?.data || []);
      setSummary(response?.summary || { active: 0, expired: 0, submitted: 0, released: 0 });
    } catch (err) {
      console.error('Failed to load shortlists', err);
      setError(err.response?.data?.error || err.message || 'Failed to load shortlist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShortlists();
  }, []);

  const groupedShortlists = useMemo(() => {
    return shortlists.reduce((acc, shortlist) => {
      const key = shortlist.vdr_id || 'Unassigned';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(shortlist);
      return acc;
    }, {});
  }, [shortlists]);

  // Load VDR application details for each group
  useEffect(() => {
    const loadVdrApplications = async () => {
      const vdrIds = Object.keys(groupedShortlists).filter(id => id !== 'Unassigned');
      for (const vdrId of vdrIds) {
        if (!vdrApplications[vdrId]) {
          try {
            const appData = await employerAPI.getApplication(vdrId);
            setVdrApplications(prev => ({ ...prev, [vdrId]: appData }));
          } catch (err) {
            console.error(`Failed to load VDR ${vdrId}`, err);
          }
        }
      }
    };
    if (Object.keys(groupedShortlists).length > 0) {
      loadVdrApplications();
    }
  }, [groupedShortlists]);

  const handleRelease = async (shortlist) => {
    const confirmed = await confirm({
      title: 'Release this worker?',
      message: `${shortlist.full_name || 'This worker'} goes back to the available pool.`,
      detail: 'Other employers can select them once released, and you may not get them back.',
      confirmLabel: 'Release worker',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      if (!shortlist.vdr_id) {
        setNotify({ show: true, type: 'error', message: 'Missing VDR ID for this shortlist record.' });
        return;
      }
      await employerAPI.releaseShortlist(shortlist.vdr_id, shortlist.id);
      await loadShortlists();
      setNotify({ show: true, type: 'success', message: 'Worker released from shortlist successfully.' });
    } catch (err) {
      console.error('Failed to release shortlist', err);
      setNotify({ show: true, type: 'error', message: err.response?.data?.error || err.message || 'Unable to release shortlist' });
    }
  };

  const toggleWorkerSelection = (vdrId, biodataId, isActive) => {
    if (!isActive) return; // Only allow selection of active shortlists

    setSelectedWorkers(prev => {
      const currentVdrSelection = prev[vdrId] || [];
      const isSelected = currentVdrSelection.includes(biodataId);
      const remaining =
        vdrApplications[vdrId]?.slots?.remaining ??
        vdrApplications[vdrId]?.slots?.localRemaining ??
        vdrApplications[vdrId]?.quota_progress?.remaining ??
        vdrApplications[vdrId]?.fclRequested ??
        0;

      if (isSelected) {
        return {
          ...prev,
          [vdrId]: currentVdrSelection.filter(id => id !== biodataId)
        };
      } else {
        const next = [...currentVdrSelection, biodataId];
        const limited =
          remaining > 0 ? next.slice(0, remaining) : next;
        return { ...prev, [vdrId]: limited };
      }
    });
  };

  const handleSubmitSelection = async (vdrId) => {
    const application = vdrApplications[vdrId];
    const selected = selectedWorkers[vdrId] || [];
    const remaining =
      application?.slots?.remaining ??
      application?.slots?.localRemaining ??
      application?.quota_progress?.remaining ??
      application?.fclRequested ??
      0;

    if (!application) {
      setNotify({ show: true, type: 'error', message: 'Application data not loaded. Please refresh the page.' });
      return;
    }

    if (selected.length === 0) {
      setNotify({ show: true, type: 'warning', message: 'Please select at least one worker to submit.' });
      return;
    }

    // BATCH SUBMISSION VALIDATION
    // If quota ≤ 50, must submit ALL remaining workers (no batching allowed)
    // If quota > 50, can submit in batches
    const quotaApproved = application?.quotaApproved || application?.quota_approved || 0;

    if (quotaApproved > 0 && quotaApproved <= 50) {
      if (remaining > 0 && selected.length < remaining) {
        setNotify({
          show: true,
          type: 'warning',
          message: `For quotas of 50 or less, you must submit all ${remaining} remaining worker(s) at once. Please select all workers before submitting.`
        });
        return;
      }
    }

    if (remaining >= 0 && selected.length > remaining) {
      setNotify({ show: true, type: 'warning', message: `You can submit up to ${remaining} worker(s) right now.` });
      return;
    }

    const batchSize = selected.length;

    try {
      setSubmitting(prev => ({ ...prev, [vdrId]: true }));
      await employerAPI.submitSelection(vdrId, selected, batchSize);
      setNotify({ show: true, type: 'success', message: `Selection submitted successfully (${batchSize}).` });
      await loadShortlists();
      setSelectedWorkers(prev => ({ ...prev, [vdrId]: [] }));
    } catch (err) {
      console.error('Submit selection failed', err);
      setNotify({ show: true, type: 'error', message: err.response?.data?.error || err.message || 'Failed to submit selection' });
    } finally {
      setSubmitting(prev => ({ ...prev, [vdrId]: false }));
    }
  };

  const selectAllActive = (vdrId) => {
    const application = vdrApplications[vdrId];
    const remaining =
      application?.slots?.remaining ??
      application?.slots?.localRemaining ??
      application?.quota_progress?.remaining ??
      application?.fclRequested ??
      0;
    const items = groupedShortlists[vdrId] || [];
    const activeIds = items
      .filter((i) => i.status === 0)
      .map((i) => i.biodata_id || i.biodata?.id)
      .filter(Boolean);
    const finalIds = remaining > 0 ? activeIds.slice(0, remaining) : activeIds;
    setSelectedWorkers((prev) => ({ ...prev, [vdrId]: finalIds }));
  };

  const deselectAll = (vdrId) => {
    setSelectedWorkers((prev) => ({ ...prev, [vdrId]: [] }));
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_LABELS[status] || STATUS_LABELS[0];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <Notifier
        type={notify.type}
        message={notify.message}
        show={notify.show}
        onClose={() => setNotify(prev => ({ ...prev, show: false }))}
        duration={3000}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`transition-all duration-300 ease-smooth pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <BreadcrumbNavigation userRole="employer" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Shortlisted Workers</h1>
                <p className="text-text-secondary">
                  View and manage workers reserved for your VDR applications
                </p>
              </div>
              <button
                onClick={loadShortlists}
                className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                <Icon name="RefreshCw" size={20} />
                <span>Refresh</span>
              </button>
            </div>

            {error && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4 text-error">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard label="Active Holds" value={summary.active} icon="Clock" accent="text-blue-600" />
              <SummaryCard label="Submitted" value={summary.submitted} icon="Send" accent="text-green-600" />
              <SummaryCard label="Expired" value={summary.expired} icon="AlertTriangle" accent="text-red-600" />
              <SummaryCard label="Released" value={summary.released} icon="Archive" accent="text-gray-500" />
              {summary.remaining !== undefined && summary.remaining !== null && (
                <SummaryCard label="Remaining Quota" value={summary.remaining} icon="CheckCircle" accent="text-emerald-600" />
              )}
            </div>

            {loading ? (
              <div className="bg-white border border-secondary-200 rounded-lg p-12 text-center">
                <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-text-secondary">Loading shortlist data...</p>
              </div>
            ) : shortlists.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Heart" size={48} className="text-secondary-400" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">No Workers Shortlisted</h3>
                <p className="text-text-secondary">Workers you shortlist will appear here for future reference</p>
              </div>
            ) : (
              Object.entries(groupedShortlists).map(([vdrId, items]) => {
                const application = vdrApplications[vdrId];
                const activeItems = items.filter(item => item.status === 0);
                const selectedCount = (selectedWorkers[vdrId] || []).length;
                const remaining =
                  application?.slots?.remaining ??
                  application?.slots?.localRemaining ??
                  application?.quota_progress?.remaining ??
                  application?.fclRequested ??
                  0;
                const isSubmitting = submitting[vdrId];

                return (
                  <div key={vdrId} className="bg-white border border-secondary-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 bg-secondary-50 border-b border-secondary-200">
                      <div>
                        <h2 className="text-lg font-semibold text-text-primary">{vdrId}</h2>
                        <div className="text-sm text-text-secondary space-y-1">
                          <div>
                            {items.length} worker{items.length === 1 ? '' : 's'} shortlisted
                            {selectedCount > 0 && ` • Selected: ${selectedCount}`}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">
                              Remaining: {remaining}
                            </span>
                            {application?.slots?.approved && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                                Approved: {application.slots.approved}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => navigate('/employer-biodata-selection', {
                            state: {
                              vdrApplication: { vdrId },
                              fromShortlist: true
                            }
                          })}
                          className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200"
                        >
                          <Icon name="Users" size={16} />
                          <span>Add More</span>
                        </button>
                        {activeItems.length > 0 && (
                          <button
                            onClick={() => handleSubmitSelection(vdrId)}
                            disabled={isSubmitting || selectedCount === 0 || (remaining >= 0 && selectedCount > remaining)}
                            className="flex items-center space-x-2 bg-primary text-white px-3 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isSubmitting ? (
                              <>
                                <Icon name="Loader" size={16} className="animate-spin" />
                                <span>Submitting...</span>
                              </>
                            ) : (
                              <>
                                <Icon name="Send" size={16} />
                                <span>Submit ({selectedCount}/{remaining || '∞'})</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <Table
                      className="border-t border-secondary-100"
                      columns={buildColumns()}
                      data={items}
                      rowKey={(row) => row.biodata_id || row.biodata?.id}
                      bulkSelection={activeItems.length > 0 ? {
                        selected: selectedWorkers[vdrId] || [],
                        onSelect: (id) => toggleWorkerSelection(vdrId, id, true),
                        onSelectAll: () => {
                          const currentSelected = selectedWorkers[vdrId] || [];
                          // If any worker is selected, deselect all; otherwise select all
                          if (currentSelected.length > 0) {
                            deselectAll(vdrId);
                          } else {
                            selectAllActive(vdrId);
                          }
                        }
                      } : null}
                      actions={(row) => (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => navigate('/employer-biodata-selection', {
                              state: { vdrApplication: { vdrId }, fromShortlist: true }
                            })}
                            className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                          >
                            <Icon name="Eye" size={16} className="mr-2" />
                            View
                          </button>
                          {row.status === 0 && (
                            <button
                              onClick={() => handleRelease(row)}
                              className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                            >
                              <Icon name="Trash" size={16} className="mr-2" />
                              Release
                            </button>
                          )}
                        </div>
                      )}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, accent }) => (
  <div className="bg-white border border-secondary-200 rounded-lg p-4 shadow-sm flex items-center gap-3">
    <div className={`w-10 h-10 rounded-full bg-secondary-50 flex items-center justify-center ${accent}`}>
      <Icon name={icon} size={18} />
    </div>
    <div>
      <div className="text-xs text-text-secondary uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold text-text-primary">{value}</div>
    </div>
  </div>
);

export default EmployerShortlist;
